import React, {
  useCallback,
  useRef,
  useEffect,
  useReducer,
  useLayoutEffect,
  useState,
} from "react";
import { useMDSHotKey } from "@/mds/domains/hotkeys";
import { COMBOBOX_REMOVE_LAST_MENTION_HOTKEY } from "@/domains/keyboard-shortcuts/shared-hotkeys";
import { MDSIcon, MDSIconKind } from "@/mds/components/icon";
import { MDSMenuItemHeader, MDSSelectMenu, MDSSelectMenuItem } from "@/mds/components/menu";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds/components/text";
import {
  MDSTextField,
  MDSTextFieldSlotName,
  MDSTextFieldVariant,
} from "@/mds/components/forms/text-field";
import { CommonEditorInput } from "./common-editor-input";
import {
  MDSComboboxProps,
  MDSComboboxSlotName,
  ComboboxState,
  MDSComboboxMention,
  MDSComboboxMode,
  MDSComboboxItem,
} from "./combobox.types";
import { comboboxReducer } from "./combobox.reducer";
import {
  StyledCombobox,
  StyledLabel,
  StyledInputContainer,
  StyledLeftContainer,
  StyledRightContainer,
  StyledInputArea,
  StyledErrorMessage,
  StyledExpand,
  StyledComboboxPopover,
  StyledPopoverContent,
  StyledEmptyResult,
  StyledLeftAddonContainer,
  StyledPopoverSearchContainer,
  StyledBottomRow,
  StyledBottomRowInner,
  StyledBottomLeftContainer,
  StyledBottomRightContainer,
  StyledDisabledInputSurface,
  StyledVoiceRow,
  StyledVoiceWaveform,
  StyledVoiceBar,
  StyledVoiceConfirmContainer,
  StyledVoiceSpinner,
} from "./combobox.style";
import {
  MemCommonMentionKind,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
} from "@mem-labs/common-editor";
import type { Selection } from "react-aria-components";
import { getSlot } from "@/mds/utils/slots";
import { JsonObject } from "type-fest";
import { MDSIconButton, MDSIconButtonSize, MDSIconButtonVariant } from "@/mds/components/buttons";
import { MentionChips } from "./mention-chips";
import { MDSComboboxListItemIcon } from "./combobox-list-item-icon";
import { MDSSelectMenuItemProps } from "@/mds/components/menu/select-menu/select-menu.types";
import { getMenuItemSubtitle } from "@/mds/components/menu/helpers";
import { ComboboxDropdown } from "./combobox-dropdown";
import { getMentionId, getSortedMentions } from "./mention-utils";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";

const ATTACH_CONTEXT_TOOLTIP_CONFIG = {
  label: "Attach a note or collection",
  placement: MDSTooltipPlacement.Top,
};

const VOICE_WAVEFORM_SAMPLE_MS = 60;
const VOICE_WAVEFORM_MIN_BAR_PX = 1;
const VOICE_WAVEFORM_MAX_BAR_PX = 18;
/** 1px bar + 3px gap, matching the mock. */
const VOICE_WAVEFORM_BAR_PITCH_PX = 4;
const VOICE_WAVEFORM_FALLBACK_BAR_COUNT = 56;

/**
 * The composer's live capture row. Amplitude is sampled on a timer rather than
 * read on render so the bar history advances at a steady rate independent of
 * how often the host re-renders. The bar count is derived from the measured
 * width so the row always spans the full gap between cancel and confirm.
 */
const ComboboxVoiceWaveform = ({
  amplitude,
  isFinalizing,
}: {
  amplitude: number;
  isFinalizing: boolean;
}) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const [barCount, setBarCount] = useState(VOICE_WAVEFORM_FALLBACK_BAR_COUNT);
  const [bars, setBars] = useState<number[]>(() =>
    new Array(VOICE_WAVEFORM_FALLBACK_BAR_COUNT).fill(0)
  );
  const amplitudeRef = useRef(amplitude);
  amplitudeRef.current = amplitude;

  useLayoutEffect(() => {
    const element = waveformRef.current;
    if (!element) return;

    const measure = () => {
      const width = element.clientWidth;
      if (width <= 0) return;
      setBarCount(Math.max(1, Math.floor(width / VOICE_WAVEFORM_BAR_PITCH_PX)));
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setBars(current => {
      if (current.length === barCount) return current;
      if (current.length > barCount) return current.slice(current.length - barCount);
      return [...new Array(barCount - current.length).fill(0), ...current];
    });
  }, [barCount]);

  // Capture has stopped and amplitude is back to 0, so advancing the history
  // would drain the waveform to a flat line under the spinner. Freeze it on
  // the last captured shape instead.
  useEffect(() => {
    if (isFinalizing) return;

    const intervalId = setInterval(() => {
      setBars(current => [...current.slice(1), amplitudeRef.current]);
    }, VOICE_WAVEFORM_SAMPLE_MS);
    return () => clearInterval(intervalId);
  }, [isFinalizing]);

  return (
    <StyledVoiceWaveform
      ref={waveformRef}
      aria-hidden="true"
      data-test-id="combobox-voice-waveform"
    >
      {bars.map((level, index) => (
        <StyledVoiceBar
          key={index}
          style={{
            /*
             * Speech RMS lands near the bottom of the 0..1 range, so a linear
             * map leaves every bar at the 1px floor. The square root spreads
             * that quiet band across the visible height.
             */
            height: `${
              VOICE_WAVEFORM_MIN_BAR_PX +
              Math.sqrt(Math.min(1, Math.max(0, level))) *
                (VOICE_WAVEFORM_MAX_BAR_PX - VOICE_WAVEFORM_MIN_BAR_PX)
            }px`,
          }}
        />
      ))}
    </StyledVoiceWaveform>
  );
};

export const MDSCombobox = <T extends MDSComboboxItem>(props: MDSComboboxProps<T>) => {
  const {
    fetchData,
    label,
    placeholder,
    errorMessage,
    isDisabled = false,
    chatIsClosed = false,
    variant,
    children,
    popoverProps,
    autoFocus = false,
    onSubmit,
    onInputFocus,
    onInputBlur,
    onInputChange,
    onDisabledClick,
    inputRef: externalInputRef,
    mode = MDSComboboxMode.Default,
    editorDimensions,
    setContent,
    onContentModified,
    onContentHtmlChanged,
    className,
    fetchIsInstant,
    mentions,
    onMentionAdd,
    onMentionRemove,
    onMentionsClear,
    implicitMentions = [],
    forceClose,
    forceCollapse,
    onLeftAddonButtonClick,
    dropdownConfig,
    voice,
  } = props;

  const [state, dispatch] = useReducer(comboboxReducer<T>, {
    isOpen: false,
    query: "",
    isEmpty: true,
    isLoading: false,
    data: [],
    hasSearched: false,
    highlightedItemId: null,
    mentions: [],
    popoverMentionMode: "implicit",
    isExpanded: false,
  } as ComboboxState<T>);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const editorSurfaceRef = useRef<HTMLDivElement>(null);
  const internalInputRef = useRef<MemCommonRichTextInputInstance>(null);
  const inputRef = externalInputRef || internalInputRef;
  const isProgrammaticFocusRef = useRef<boolean>(false);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  // Track ongoing requests to handle race conditions
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track when the bottom right addon (e.g., model dropdown) is open
  const [isBottomRightAddonOpen, setIsBottomRightAddonOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  // Track isEmpty in a ref for use in blur timeout
  const isEmptyRef = useRef(true);
  // Track dropdown open state in a ref for use in blur timeout
  const isDropdownOpenRef = useRef(false);
  // Brief grace period after dropdown closes to prevent blur handler from collapsing
  const dropdownCloseGraceRef = useRef(false);

  // Set the content of the editor
  useLayoutEffect(() => {
    if (!setContent) return;

    // Wait for editor to be initialized.
    const h = setTimeout(() => {
      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.SetEditorContent,
        payload: { content: setContent },
      });
    }, 10);

    return () => clearTimeout(h);
  }, [inputRef, setContent]);

  // Handle external force close
  useEffect(() => {
    if (forceClose && state.isOpen) {
      dispatch({ type: "POPOVER_CLOSED" });
    }
  }, [forceClose, state.isOpen]);

  // Handle external force collapse - collapses the combobox and blurs the input
  useEffect(() => {
    if (forceCollapse) {
      dispatch({ type: "SET_EXPANDED", payload: false });
      const activeElement = document.activeElement as HTMLElement | null;
      if (rootRef.current?.contains(activeElement)) {
        activeElement?.blur();
      }
    }
  }, [forceCollapse]);

  useEffect(() => {
    if (!isDisabled) return;

    dispatch({ type: "SET_EXPANDED", payload: false });
    const activeElement = document.activeElement as HTMLElement | null;
    if (rootRef.current?.contains(activeElement)) {
      activeElement?.blur();
    }
  }, [isDisabled]);

  useEffect(() => {
    const editorSurface = editorSurfaceRef.current;
    if (!editorSurface) return;

    if (isDisabled) {
      editorSurface.setAttribute("inert", "");
      editorSurface.setAttribute("aria-hidden", "true");
      return;
    }

    editorSurface.removeAttribute("inert");
    editorSurface.removeAttribute("aria-hidden");
  }, [isDisabled]);

  const cleanupLoadingTimeout = () => {
    if (!loadingTimeoutRef.current) {
      return;
    }

    clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = null;
  };

  // Emit input height to consumer on content changes
  const emitInputHeightChange = useCallback(() => {
    const containerEl = inputContainerRef.current;
    const measuredHeight = containerEl?.offsetHeight;
    if (measuredHeight && typeof measuredHeight === "number") {
      onInputChange?.(measuredHeight);
    } else {
      onInputChange?.();
    }
  }, [onInputChange]);

  const handleContainerClick = useCallback(
    (e: React.MouseEvent) => {
      if (isDisabled) {
        onDisabledClick?.();
        return;
      }

      // Only focus if clicking directly on the container or its immediate layout wrappers,
      // not if clicking on interactive children that bubbled up
      if (e.target !== e.currentTarget) {
        return;
      }

      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.FocusEditor,
        payload: null,
      });
    },
    [inputRef, isDisabled, onDisabledClick]
  );

  const handleDisabledInputSurfacePressStart = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDisabledInputSurfaceClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDisabledClick?.();
  }, [onDisabledClick]);

  // Handle mention queries
  const handleMentionQuery = useCallback(
    async (popoverMentionMode: "explicit" | "implicit", queryStr?: string) => {
      dispatch({ type: "QUERY_CHANGED", payload: queryStr ?? "" });

      const trimmedQuery = queryStr?.trim() ?? "";

      // Check if query starts with mention symbols
      const startsWithMentionSymbol = ["@", "#"].includes(trimmedQuery.charAt(0));

      if (!startsWithMentionSymbol && popoverMentionMode === "implicit") {
        // Close popover and clear data if no mention symbol
        dispatch({ type: "RESET" });

        // Clear any pending loading timeout
        cleanupLoadingTimeout();

        // Cancel any ongoing request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        return;
      }

      const mentionSymbol = trimmedQuery.charAt(0);
      let query = trimmedQuery;
      if (popoverMentionMode === "implicit") {
        query = trimmedQuery.slice(1); // Remove the @ or # symbol
      }

      // We want to load even if the query is empty
      if (trimmedQuery.length >= 0) {
        // Cancel any previous request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }

        // Clear any pending loading timeout
        cleanupLoadingTimeout();

        // Start new search - this resets state and closes popover
        dispatch({ type: "SEARCH_STARTED" });
        dispatch({ type: "POPOVER_MENTION_MODE_CHANGED", payload: popoverMentionMode });

        // Create new abort controller for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        // Only set loader if we expect long fecthing times
        if (!fetchIsInstant) {
          dispatch({ type: "LOADING_STARTED" });
        }

        try {
          const result = await fetchData({ query, mentionSymbol });

          // Only update if this request wasn't cancelled
          if (!abortController.signal.aborted) {
            dispatch({ type: "SEARCH_COMPLETED", payload: result.data });
            dispatch({ type: "LOADING_STOPPED" });
            cleanupLoadingTimeout();
          }
        } catch (_error) {
          if (abortController.signal.aborted) {
            return;
          }

          dispatch({ type: "SEARCH_FAILED" });
          dispatch({ type: "LOADING_STOPPED" });
          cleanupLoadingTimeout();
        }
      }
    },

    [fetchData, fetchIsInstant]
  );

  // Update popover width to match input container
  useLayoutEffect(() => {
    if (inputContainerRef.current && popoverContentRef.current) {
      const containerWidth = inputContainerRef.current.offsetWidth;
      popoverContentRef.current.style.setProperty(
        "--combobox-popover-trigger-width",
        `${containerWidth}px`
      );
    }
  }, [state.isOpen, state.data.length, state.hasSearched]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Close the explicit (plus-button) popover on any pointerdown outside it.
  // React-aria's own outside dismissal waits for a `click` after pointerdown,
  // but iOS WebKit never synthesizes a click for taps on non-interactive
  // content — and the modal popover marks the rest of the app inert — so on
  // mobile web the menu was impossible to dismiss. Pointerdown always fires.
  useEffect(() => {
    if (!state.isOpen || state.popoverMentionMode !== "explicit") return;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (popoverContentRef.current?.contains(target)) return;
      if (rootRef.current?.contains(target)) return;

      // Match the focus restore react-aria's own dismissal runs via
      // onOpenChange — except on touch, where refocusing would pop the
      // on-screen keyboard right after the user tapped outside to dismiss.
      if (e.pointerType !== "touch") {
        inputRef.current?.dispatchAction({
          kind: MemCommonRichTextInputActionKind.FocusEditor,
          payload: null,
        });
      }

      dispatch({ type: "POPOVER_CLOSED" });
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [state.isOpen, state.popoverMentionMode, inputRef]);

  // Handle editor empty state changes
  const handleIsEmptyChange = useCallback((empty: boolean) => {
    isEmptyRef.current = empty;
    dispatch({ type: "EMPTY_STATE_CHANGED", payload: empty });
  }, []);

  const handleSelection = async (keys: Selection) => {
    const selected = keys as Set<string>;
    const uuid = selected.values().next().value;
    const item = state.data.find(item => item.id === uuid);

    if (!item) return;

    const label = (await item.onClick?.()) ?? item.label;

    const kind = (() => {
      if (item.kind === "create-new-collection") {
        return MemCommonMentionKind.Collection;
      }
      return item.kind ?? MemCommonMentionKind.Note;
    })();

    if (state.popoverMentionMode === "explicit") {
      // Plus button mode - add to mentions via handler
      let mention: MDSComboboxMention;

      if (kind === MemCommonMentionKind.Collection) {
        mention = {
          kind: MemCommonMentionKind.Collection,
          isExternal: false,
          collection_id: item.id,
          collection_title: label,
        };
      } else {
        mention = {
          kind: MemCommonMentionKind.Note,
          isExternal: false,
          note_id: item.id,
          note_primary_label: label,
        };
      }

      onMentionAdd?.(mention);
    } else {
      // Implicit mode - insert into input text
      inputRef.current?.dispatchAction?.({
        kind: MemCommonRichTextInputActionKind.CloseMention,
        payload: {
          id: item?.id,
          kind,
          label,
          // action,
        },
      });

      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.FocusEditor,
        payload: null,
      });
    }

    // Clear highlight after selection
    dispatch({ type: "HIGHLIGHT_CHANGED", payload: null });
    dispatch({ type: "POPOVER_CLOSED" });
    dispatch({ type: "QUERY_CHANGED", payload: "" });
  };

  const sendSubmitAction = useCallback(() => {
    if (mode === MDSComboboxMode.Passive || isDisabled) return;

    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SendContents,
      payload: null,
    });
  }, [inputRef, isDisabled, mode]);

  const handleArrowKeyPressed = useCallback(
    (direction: "up" | "down") => {
      if (!state.isOpen || state.data.length === 0) return;

      // Don't focus listbox - keep focus in input for continued typing
      // Instead, update highlighted item using reducer actions
      if (direction === "down") {
        dispatch({ type: "HIGHLIGHT_NEXT" });
      } else {
        dispatch({ type: "HIGHLIGHT_PREVIOUS" });
      }
    },
    [state.isOpen, state.data.length]
  );

  const handleEnterPressed = useCallback(async () => {
    if (isDisabled) return;

    // Context 1: If there's a highlighted item, select it (don't submit)
    if (state.highlightedItemId && state.data.length > 0) {
      const item = state.data.find(item => item.id === state.highlightedItemId);

      if (!item) {
        // Exit early - don't submit
        return;
      }

      const label = (await item.onClick?.()) ?? item.label;

      const kind = (() => {
        if (item.kind === "create-new-collection") {
          return MemCommonMentionKind.Collection;
        }
        return item.kind ?? MemCommonMentionKind.Note;
      })();

      if (state.popoverMentionMode === "explicit") {
        // Plus button mode - add to mentions via handler
        let mention: MDSComboboxMention;

        if (kind === MemCommonMentionKind.Collection) {
          mention = {
            kind: MemCommonMentionKind.Collection,
            isExternal: false,
            collection_id: item.id,
            collection_title: label,
          };
        } else {
          mention = {
            kind: MemCommonMentionKind.Note,
            isExternal: false,
            note_id: item.id,
            note_primary_label: label,
          };
        }

        onMentionAdd?.(mention);
      } else {
        // Implicit mode - insert into input text
        inputRef.current?.dispatchAction?.({
          kind: MemCommonRichTextInputActionKind.CloseMention,
          payload: {
            id: item?.id,
            kind,
            label,
          },
        });

        inputRef.current?.dispatchAction({
          kind: MemCommonRichTextInputActionKind.FocusEditor,
          payload: null,
        });
      }

      // Clear highlight after selection
      dispatch({ type: "HIGHLIGHT_CHANGED", payload: null });
      dispatch({ type: "POPOVER_CLOSED" });
      dispatch({ type: "QUERY_CHANGED", payload: "" });

      // Exit early after successful selection - don't submit form
      return;
    }

    // Context 2: No highlighted item - submit the form (only in implicit mode)
    if (state.popoverMentionMode === "implicit") {
      sendSubmitAction();
    }
  }, [
    isDisabled,
    state.highlightedItemId,
    state.data,
    state.popoverMentionMode,
    inputRef,
    sendSubmitAction,
    onMentionAdd,
  ]);

  const handleMentionRemove = useCallback(
    (mentionId: string) => {
      onMentionRemove?.(mentionId);
    },
    [onMentionRemove]
  );

  useMDSHotKey(
    COMBOBOX_REMOVE_LAST_MENTION_HOTKEY,
    event => {
      const lastMention = getSortedMentions(mentions).at(-1);
      if (!lastMention) {
        return;
      }

      event.preventDefault();
      onMentionRemove?.(getMentionId(lastMention));
    },
    {
      enabled: isInputFocused && state.isEmpty && mentions.length > 0 && !isDisabled,
      target: rootRef,
      ignoreInputs: false,
      preventDefault: true,
    }
  );

  const handlePlusButtonClick = useCallback(() => {
    if (isDisabled) return;
    onLeftAddonButtonClick?.();
    handleMentionQuery("explicit");
  }, [isDisabled, handleMentionQuery, onLeftAddonButtonClick]);

  // Handle input focus - expand the combobox
  const handleInputFocus = useCallback(() => {
    setIsInputFocused(true);
    dispatch({ type: "SET_EXPANDED", payload: true });
    onInputFocus?.();
  }, [onInputFocus]);

  // Handle input blur - collapse if no content and dropdown is closed
  const handleInputBlur = useCallback(() => {
    // Use a timeout to allow dropdown click to register before collapsing
    // We use refs to get the current state at the time the timeout fires
    setTimeout(() => {
      // Check if focus is still within the combobox container or popover
      const rootElement = rootRef.current;
      const popoverElement = popoverContentRef.current;
      const activeElement = document.activeElement;
      const isFocusWithinCombobox = rootElement?.contains(activeElement);
      const isFocusWithinPopover = popoverElement?.contains(activeElement);

      // Don't collapse if:
      // - Focus is still within the combobox (user clicked on dropdown, plus button, etc.)
      // - Focus is within the popover (mention search)
      // - Dropdown (model selector) is open
      // - There's content in the input
      if (
        isFocusWithinCombobox ||
        isFocusWithinPopover ||
        isDropdownOpenRef.current ||
        !isEmptyRef.current
      ) {
        return;
      }

      if (!dropdownCloseGraceRef.current) {
        dispatch({ type: "SET_EXPANDED", payload: false });
      }
    }, 150);
  }, []);

  const handleImmediateInputBlur = useCallback(() => {
    setIsInputFocused(false);
    handleInputBlur();
  }, [handleInputBlur]);

  // Prevent mousedown on non-input elements from stealing focus from the input
  // This keeps the input focused when clicking buttons, dropdowns, etc. inside the combobox
  const handleMouseDownPreventFocusLoss = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    // Allow normal focus behavior for:
    // 1. The ProseMirror editor itself (has .ProseMirror class or is inside it)
    // 2. Any input elements (for the popover search field)
    const isEditorElement = target.closest(".ProseMirror");
    const isInputElement = target.tagName === "INPUT" || target.tagName === "TEXTAREA";

    if (isEditorElement || isInputElement) {
      return;
    }

    // For all other elements (buttons, dropdowns, etc.), prevent focus stealing
    // The click event will still fire normally
    e.preventDefault();
  }, []);

  // Callback for when the dropdown opens/closes
  const handleDropdownOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsBottomRightAddonOpen(isOpen);
      isDropdownOpenRef.current = isOpen;
      dropdownConfig?.onOpenChange?.(isOpen);

      setTimeout(() => {
        inputRef.current?.dispatchAction({
          kind: MemCommonRichTextInputActionKind.FocusEditor,
          payload: null,
        });
      }, 0);

      // Prevent the blur handler's 150ms timeout from collapsing the combobox
      // while focus is being restored after dropdown close.
      if (!isOpen) {
        dropdownCloseGraceRef.current = true;
        setTimeout(() => {
          dropdownCloseGraceRef.current = false;
        }, 200);
      }
    },
    [inputRef, dropdownConfig]
  );

  // Handle selection from the dropdown
  const handleDropdownSelect = useCallback(
    (optionId: string) => {
      if (!dropdownConfig) return;

      dropdownConfig.onSelect(optionId);
      setIsBottomRightAddonOpen(false);
      isDropdownOpenRef.current = false;

      setTimeout(() => {
        inputRef.current?.dispatchAction({
          kind: MemCommonRichTextInputActionKind.FocusEditor,
          payload: null,
        });
      }, 0);
    },
    [dropdownConfig, inputRef]
  );

  const handleSubmit = useCallback(
    async (payload: {
      mdxContent: string;
      htmlContent: string;
      markdownContent: string;
      tiptapJsonContent: JsonObject;
    }) => {
      const { mdxContent, htmlContent, tiptapJsonContent } = payload;
      if (isDisabled) return;

      // Parse mentions from the markdownContent and merge with external and implicit mentions
      const inputMentions = parseMentionsFromMarkdown(mdxContent);
      const allMentions = [...mentions, ...inputMentions];

      // Only submit if there's content
      if (mdxContent.trim()) {
        const success = await onSubmit?.({
          htmlContent,
          mdxContent,
          tiptapJsonContent,
          mentions: allMentions,
          implicitMentions,
        });

        if (mode === MDSComboboxMode.Passive) return;

        // Clear mentions via handler
        onMentionsClear?.();
        // TODO: the chat input appears to get cleared before we even get here

        if (success) {
          // Clear the input after successful submit
          inputRef.current?.dispatchAction?.({
            kind: MemCommonRichTextInputActionKind.SetEditorContent,
            payload: {
              content: "",
            },
          });
        } else {
          // restore the draft message
          inputRef.current?.dispatchAction?.({
            kind: MemCommonRichTextInputActionKind.SetEditorContent,
            payload: {
              content: htmlContent.replace(/(<p>\s*<\/p>\s*)$/i, ""), // the regex removes the "enter" line if the user submitted via the enter key
            },
          });
        }
      }
    },
    [isDisabled, mode, onSubmit, inputRef, mentions, implicitMentions, onMentionsClear]
  );

  const canSend = !isDisabled && !state.isEmpty;
  // Chips are only visible when the chat panel is open
  const hasVisibleMentions = mentions.length > 0 && !chatIsClosed;
  // Treat the combobox as visually expanded when focused OR when visible mention chips are present
  const isVisuallyExpanded = state.isExpanded || hasVisibleMentions;
  // The expanded two-row layout (stretch alignment, left + swapped into the
  // bottom row) only exists when a dropdownConfig renders that bottom row;
  // without it the input stays a single centered row even while focused.
  // Voice mode is a single row, so it never takes the expanded two-row layout.
  const hasExpandedBottomRow = !!dropdownConfig && isVisuallyExpanded && !voice;

  // Content display conditions for popover
  const isLoading = state.isLoading;
  const hasResults = state.data.length > 0;
  const showEmptyResults = state.data.length === 0 && state.hasSearched && !state.isLoading;
  // In implicit mode the raw query still carries the leading @/# symbol
  const trimmedQuery = state.query.trim();
  const isUnqueried =
    (state.popoverMentionMode === "implicit" && ["@", "#"].includes(trimmedQuery.charAt(0))
      ? trimmedQuery.slice(1).trim()
      : trimmedQuery) === "";

  // Show popover only when we have content to display
  // This prevents empty popover flashes during state transitions
  const shouldShowPopover = state.isOpen;

  return (
    <StyledCombobox
      ref={rootRef}
      data-mds-name="combobox"
      translate="no"
      className={className}
      onMouseDown={handleMouseDownPreventFocusLoss}
    >
      {label && <StyledLabel>{label}</StyledLabel>}

      <StyledInputContainer
        ref={inputContainerRef}
        variant={variant}
        hasLeftActionButton={true}
        aria-disabled={isDisabled}
        $isExpanded={hasExpandedBottomRow}
        $hasMentions={hasVisibleMentions}
        $isRecording={!!voice}
        data-mds-name="combobox-input-container"
        onClick={handleContainerClick}
      >
        {/* Left addon - cancel while recording, otherwise the plus button */}
        <StyledLeftContainer>
          {voice ? (
            <MDSIconButton
              iconKind={MDSIconKind.Exit}
              variant={MDSIconButtonVariant.Default}
              onClick={voice.onCancel}
              aria-label="Discard voice input"
              data-test-id="combobox-voice-cancel-button"
              rounded
              size={MDSIconButtonSize.Small}
            />
          ) : (
            <>
              <StyledLeftAddonContainer $isExpanded={hasExpandedBottomRow}>
                <MDSTooltip config={ATTACH_CONTEXT_TOOLTIP_CONFIG}>
                  <MDSIconButton
                    iconKind={MDSIconKind.Plus}
                    variant={MDSIconButtonVariant.Default}
                    onClick={handlePlusButtonClick}
                    isDisabled={isDisabled}
                    data-test-id="combobox-plus-button"
                    rounded
                    size={MDSIconButtonSize.Small}
                  />
                </MDSTooltip>
              </StyledLeftAddonContainer>

              {getSlot(children, MDSComboboxSlotName.LeftAddon)}
            </>
          )}
        </StyledLeftContainer>

        {/* Main input area */}
        <StyledInputArea data-mds-name="combobox-input-area">
          {!chatIsClosed && !voice && (
            <MentionChips
              isDisabled={isDisabled}
              mentions={mentions}
              onRemove={handleMentionRemove}
              onInputChange={emitInputHeightChange}
            />
          )}

          {/*
           * Recording hides the editor rather than unmounting it: unmounting
           * would tear down the ProseMirror instance and lose any draft the
           * user typed before holding the talk key.
           */}
          <StyledExpand
            ref={editorSurfaceRef}
            $isInert={isDisabled}
            $isExpanded={hasExpandedBottomRow}
            $isHidden={!!voice}
          >
            <CommonEditorInput
              dimensions={editorDimensions}
              onMentionQuerySet={query => handleMentionQuery("implicit", query)}
              onIsEmptyChange={handleIsEmptyChange}
              placeholder={placeholder}
              isDisabled={isDisabled}
              inputRef={inputRef}
              onSubmit={handleSubmit}
              onArrowKeyPressed={handleArrowKeyPressed}
              onEnterPressed={handleEnterPressed}
              onInputFocus={handleInputFocus}
              onImmidiateBlur={handleImmediateInputBlur}
              onInputBlur={onInputBlur}
              onInputChange={emitInputHeightChange}
              onContentModified={onContentModified}
              onContentHtmlChanged={onContentHtmlChanged}
              autoFocus={autoFocus}
              autoSubmit={mode !== MDSComboboxMode.Passive}
            />
          </StyledExpand>

          {voice && (
            <StyledVoiceRow role="status" aria-label={voice.isFinalizing ? "Finishing up" : "Listening"}>
              <ComboboxVoiceWaveform
                amplitude={voice.amplitude}
                isFinalizing={voice.isFinalizing}
              />
            </StyledVoiceRow>
          )}

          {dropdownConfig && !voice && (
            <StyledBottomRow $isExpanded={isVisuallyExpanded} data-test-id="combobox-bottom-row">
              <StyledBottomRowInner $isExpanded={isVisuallyExpanded}>
                <StyledBottomLeftContainer>
                  <MDSTooltip config={ATTACH_CONTEXT_TOOLTIP_CONFIG}>
                    <MDSIconButton
                      iconKind={MDSIconKind.Plus}
                      variant={MDSIconButtonVariant.Default}
                      onClick={handlePlusButtonClick}
                      isDisabled={isDisabled}
                      data-test-id="combobox-plus-button-expanded"
                      rounded
                      size={MDSIconButtonSize.Small}
                    />
                  </MDSTooltip>
                </StyledBottomLeftContainer>
                <StyledBottomRightContainer>
                  <ComboboxDropdown
                    config={dropdownConfig}
                    isOpen={isBottomRightAddonOpen}
                    onOpenChange={handleDropdownOpenChange}
                    onSelect={handleDropdownSelect}
                  />
                </StyledBottomRightContainer>
              </StyledBottomRowInner>
            </StyledBottomRow>
          )}
        </StyledInputArea>

        {/* Right addon - confirm while recording, otherwise send or custom slot */}
        {voice ? (
          <StyledVoiceConfirmContainer>
            {voice.isFinalizing ? (
              <StyledVoiceSpinner
                role="status"
                aria-label="Finishing up"
                data-test-id="combobox-voice-spinner"
              />
            ) : (
              <MDSIconButton
                iconKind={MDSIconKind.Check}
                onClick={voice.onConfirm}
                variant={MDSIconButtonVariant.Brand}
                aria-label="Send voice input"
                rounded
                data-test-id="combobox-voice-confirm-button"
              />
            )}
          </StyledVoiceConfirmContainer>
        ) : (
          mode !== MDSComboboxMode.Passive && (
            <StyledRightContainer $variant={variant}>
              <MDSIconButton
                iconKind={MDSIconKind.ArrowBrand}
                onClick={sendSubmitAction}
                isDisabled={!canSend}
                variant={MDSIconButtonVariant.Brand}
                rounded
                data-test-id="combobox-submit-button"
              />
              {/* Custom right addon slot */}
              {getSlot(children, MDSComboboxSlotName.RightAddon)}
            </StyledRightContainer>
          )
        )}

        {isDisabled ? (
          <StyledDisabledInputSurface
            type="button"
            tabIndex={-1}
            aria-label="Input unavailable"
            data-test-id="combobox-disabled-input-surface"
            onPointerDown={handleDisabledInputSurfacePressStart}
            onMouseDown={handleDisabledInputSurfacePressStart}
            onClick={handleDisabledInputSurfaceClick}
          />
        ) : null}
      </StyledInputContainer>

      {errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}

      <StyledComboboxPopover
        isNonModal={state.popoverMentionMode === "implicit"}
        triggerRef={inputContainerRef}
        isOpen={shouldShowPopover}
        onOpenChange={isOpen => {
          // Don't close popover if we're programmatically focusing the listbox
          if (!isOpen && isProgrammaticFocusRef.current) {
            return;
          }

          // When popover closes, focus the input
          if (!isOpen) {
            inputRef.current?.dispatchAction({
              kind: MemCommonRichTextInputActionKind.FocusEditor,
              payload: null,
            });
          }

          dispatch({ type: isOpen ? "POPOVER_OPENED" : "POPOVER_CLOSED" });
        }}
        placement="bottom start"
        data-mds-name="combobox-popover"
        {...popoverProps}
      >
        <StyledPopoverContent ref={popoverContentRef}>
          {state.popoverMentionMode === "explicit" && (
            <StyledPopoverSearchContainer>
              <MDSTextField
                placeholder="Filter"
                value={state.query}
                onChange={query => handleMentionQuery("explicit", query)}
                onClearText={() => handleMentionQuery("explicit", "")}
                variant={MDSTextFieldVariant.Filter}
                showClearTextButton
                inputProps={{
                  autoFocus: !isMobile(),
                }}
              >
                <div slot={MDSTextFieldSlotName.LeftAddon}>
                  <MDSIcon kind={MDSIconKind.Search} />
                </div>
              </MDSTextField>
            </StyledPopoverSearchContainer>
          )}

          {hasResults && (
            <MDSSelectMenu
              onSelectionChange={handleSelection}
              selectionMode="single"
              shouldFocusWrap={true}
              selectedKeys={(() => {
                const selectedKeys: Set<string> = state.highlightedItemId
                  ? new Set([state.highlightedItemId])
                  : new Set();

                return selectedKeys;
              })()}
              data-mds-name="combobox-list-box"
              aria-label="Combobox"
              // The menu's own width is max-content (shrink-wrap); inside the
              // popover it must fill the trigger-width content box or item
              // rows render narrower than the dropdown.
              style={{ maxHeight: "75vh", width: "100%" }}
            >
              {state.data.map((item: MDSComboboxItem) => {
                if (item.isHeader) {
                  const label =
                    item.kind === MemCommonMentionKind.Note
                      ? "Mention a note"
                      : "Mention a collection";
                  return <MDSMenuItemHeader key={item.id} label={label} />;
                }

                const selectMenuItem: MDSSelectMenuItemProps = {
                  id: item.id,
                  label: item.label,
                  subtitle: getMenuItemSubtitle(item.kind as "collection" | "note", item.metadata),
                  icon: () => (
                    <MDSComboboxListItemIcon
                      kind={item.kind}
                      collectionId={item.metadata?.collectionId as string | undefined}
                    />
                  ),
                  onClick: item.onClick,
                };
                return <MDSSelectMenuItem key={item.id} {...selectMenuItem} />;
              })}
            </MDSSelectMenu>
          )}
          {showEmptyResults && (
            <StyledEmptyResult data-test-id="combobox-nothing-found">
              <MDSText
                variant={MDSTextVariant.TextS}
                color={MDSTextColor.Secondary}
                numberOfLines={2}
              >
                {isUnqueried
                  ? "You can mention your notes and collections here for context"
                  : "Nothing found"}
              </MDSText>
            </StyledEmptyResult>
          )}
          {isLoading && (
            <StyledEmptyResult data-test-id="combobox-loading">Loading...</StyledEmptyResult>
          )}
        </StyledPopoverContent>
      </StyledComboboxPopover>
    </StyledCombobox>
  );
};

// Function to parse mentions from markdownContent
const parseMentionsFromMarkdown = (markdownContent: string): MDSComboboxMention[] => {
  const mentions: MDSComboboxMention[] = [];
  const mentionRegex = /<a[^>]*data-type="mention-(note|collection)"[^>]*>/g;

  let match;
  while ((match = mentionRegex.exec(markdownContent)) !== null) {
    const anchorTag = match[0];

    // Extract data attributes
    const idMatch = anchorTag.match(/data-mention-id="([^"]+)"/);
    const kindMatch = anchorTag.match(/data-mention-kind="([^"]+)"/);
    const labelMatch = anchorTag.match(/data-mention-fallback-label="([^"]+)"/);

    if (idMatch && kindMatch && labelMatch) {
      const id = idMatch[1];
      const kind = kindMatch[1];
      const label = labelMatch[1];

      // Convert string kind to MemCommonMentionKind enum value
      if (kind === "collection") {
        mentions.push({
          kind: MemCommonMentionKind.Collection,
          collection_id: id,
          collection_title: label,
          isExternal: false,
        });
      } else if (kind === "note") {
        mentions.push({
          kind: MemCommonMentionKind.Note,
          note_id: id,
          note_primary_label: label,
          isExternal: false,
        });
      }
    }
  }

  return mentions;
};

const isMobile = () => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  return /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    navigator?.userAgent?.toLowerCase()
  );
};
